import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import PlaceCard, { type PlaceCardLayoutMode } from '@web/src/features/place/place-card/place-card'
import {
  type PlaceListFilterFormValues,
  placeListFilterFormValuesFromResolvedParams,
} from '@web/src/features/place/place-list-filter-types'
import PlaceListSideNav from '@web/src/features/place/place-list-side-nav/place-list-side-nav'
import PlaceListTopNav from '@web/src/features/place/place-list-top-nav/place-list-top-nav'
import PlaceMapViewButton from '@web/src/features/place/place-map-view-button/place-map-view-button'
import PlacesMenu from '@web/src/features/place/places-menu/places-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import { LayoutGridIcon, LayoutListIcon, MapPinHouseIcon, PlusIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AttachedMediaInfo, PlaceInfo } from 'src/lib/fragments'
import type { PlaceCategoryEnum, PlaceSortEnum, PlacesQuery, PlacesQueryVariables } from 'types/graphql'
import { z } from 'zod'
import { DEFAULT_PLACE_LIST_DISTANCE, placeListPageHeadFromLoaderData } from './list.seo'

const PLACES_QUERY: TypedDocumentNode<PlacesQuery, PlacesQueryVariables> = gql`
  query PlacesQuery($page: Int, $filter: PlacesFilter, $sortBy: PlaceSortEnum) {
    placesPage(page: $page, filter: $filter, sortBy: $sortBy) {
      places {
        ...PlaceInfo
        firstMedia {
          ...AttachedMediaInfo
        }
      }
      count
      perPage
    }
    placeTags {
      id
      name
    }
  }
  ${PlaceInfo}
  ${AttachedMediaInfo}
`

type PlacesListLoaderDeps = {
  page: number
  name?: string
  distance: number
  tags?: string[]
  categories?: string[]
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  myPlaces: boolean
  needsReview: boolean
}

function placesQueryVariablesFromDeps(deps: PlacesListLoaderDeps): PlacesQueryVariables {
  return {
    page: deps.page,
    filter: {
      name: deps.name,
      distance: deps.distance,
      tags: deps.tags,
      categories: deps.categories as PlaceCategoryEnum[],
      location: deps.location,
      locationLat: deps.locationLat,
      locationLong: deps.locationLong,
      myPlaces: deps.myPlaces,
      needsReview: deps.needsReview,
    },
    sortBy: deps.sortBy as PlaceSortEnum,
  }
}

type PlaceListSearchInput = {
  page?: number
  name?: string
  distance?: number
  tags?: string[]
  categories?: string[]
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  myPlaces?: boolean
  needsReview?: boolean
}

/** URL search may omit params; align loader and client query with the same fallbacks. */
function resolvePlaceListSearchParams(search: PlaceListSearchInput): PlacesListLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    distance: search.distance ?? DEFAULT_PLACE_LIST_DISTANCE,
    tags: search.tags,
    categories: search.categories,
    location: search.location,
    locationLat: search.locationLat,
    locationLong: search.locationLong,
    sortBy: search.sortBy,
    myPlaces: search.myPlaces ?? false,
    needsReview: search.needsReview ?? false,
  }
}

export const Route = createFileRoute('/(public)/(main-layout)/place/list')({
  component: PlaceSearchPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    distance: z.coerce.number().optional(),
    tags: z.array(z.string()).optional(),
    categories: z.array(z.string()).optional(),
    location: z.string().optional(),
    locationLat: z.coerce.number().optional(),
    locationLong: z.coerce.number().optional(),
    sortBy: z.string().optional(),
    myPlaces: z.coerce.boolean().optional(),
    needsReview: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        distance: DEFAULT_PLACE_LIST_DISTANCE,
        myPlaces: false,
        needsReview: false,
        sortBy: 'DISTANCE',
        tags: [],
        categories: [],
        location: '',
        locationLat: 0,
        locationLong: 0,
      }),
    ],
  },
  head: ({ loaderData, match }) => placeListPageHeadFromLoaderData(loaderData, match.search),
  loaderDeps: ({ search }) => resolvePlaceListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as PlacesListLoaderDeps
    const { queryData, queryRef } = await queryAndPreload(context, PLACES_QUERY, {
      variables: placesQueryVariablesFromDeps(deps),
    })
    const places = queryData?.placesPage?.places ?? []
    return {
      placesQueryRef: queryRef,
      placesQueryData: queryData,
      seoList: {
        items: places.slice(0, 24).map((p) => ({
          name: p.name?.trim() || 'Place',
          path: `/place/${p.slug ?? p.id}`,
          imageUrl: p.firstMedia?.imageUrlMedium,
        })),
        totalCount: queryData?.placesPage?.count ?? 0,
      },
    }
  },
})

function CreatePlaceButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const handleClick = () => {
    if (hasPermission('place:create')) {
      navigate({ to: '/place/create' })
    } else {
      navigate({ to: '/supporter' })
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<Button aria-label={t('Create a new place')} onClick={handleClick} size="icon" type="button" />}
      >
        <PlusIcon strokeWidth={3} />
      </TooltipTrigger>
      <TooltipContent>{t('Create a new place')}</TooltipContent>
    </Tooltip>
  )
}

function LayoutToggleButton({ layout, onToggle }: { layout: PlaceCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={layout === 'compact' ? t('Switch to stacked place cards') : t('Switch to compact place cards')}
            onClick={onToggle}
            size="icon"
            type="button"
            variant="secondary"
          />
        }
      >
        {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function PlaceSearchPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolvePlaceListSearchParams(search), [search])
  const { data, error } = useReadQuery(loaderData.placesQueryRef as QueryRef<PlacesQuery>)

  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const { currentUser, hasPermission, isAuthenticated, loading: authLoading } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated && resolvedSearch.page > 2) {
      navigate({ to: '/auth/signup', replace: true } as never)
    }
  }, [authLoading, isAuthenticated, navigate, resolvedSearch.page])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const [placeCardLayout, setPlaceCardLayout] = useState<PlaceCardLayoutMode>('regular')

  const resolvedPlacesQuery = data //?? loaderData.placesQueryData
  const resolvedPlacesPage =
    resolvedPlacesQuery?.placesPage ??
    ({
      places: [],
      count: 0,
      perPage: 1,
    } as NonNullable<PlacesQuery['placesPage']>)
  const resolvedPlaceTags = resolvedPlacesQuery?.placeTags ?? []

  const listFilterDefaults = useMemo(
    (): PlaceListFilterFormValues =>
      placeListFilterFormValuesFromResolvedParams({
        name: search.name,
        distance: resolvedSearch.distance,
        tags: search.tags,
        categories: search.categories,
        location: search.location ?? currentUser?.profile?.location,
        locationLat: search.locationLat ?? currentUser?.profile?.locationLat,
        locationLong: search.locationLong ?? currentUser?.profile?.locationLong,
        sortBy: search.sortBy,
        myPlaces: resolvedSearch.myPlaces,
        needsReview: resolvedSearch.needsReview,
      }),
    [
      search.name,
      resolvedSearch.distance,
      search.tags,
      search.categories,
      search.location,
      search.locationLat,
      search.locationLong,
      resolvedSearch.myPlaces,
      resolvedSearch.needsReview,
      search.sortBy,
      currentUser?.profile?.location,
      currentUser?.profile?.locationLat,
      currentUser?.profile?.locationLong,
    ]
  )

  const listFilterResetValues = useMemo(
    (): PlaceListFilterFormValues =>
      placeListFilterFormValuesFromResolvedParams({
        name: undefined,
        distance: 10_000,
        tags: undefined,
        categories: undefined,
        location: currentUser?.profile?.location,
        locationLat: currentUser?.profile?.locationLat,
        locationLong: currentUser?.profile?.locationLong,
        sortBy: undefined,
        myPlaces: false,
        needsReview: false,
      }),
    [currentUser?.profile?.location, currentUser?.profile?.locationLat, currentUser?.profile?.locationLong]
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: search.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: search.sortBy })
  }, [search.sortBy, sortFormMethods])

  const applyListFilters = useCallback(
    (values: PlaceListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          distance: values.distance?.[0] < 1000 ? values.distance[0] : undefined,
          tags: values.tags?.length > 0 ? values.tags : undefined,
          categories: values.categories?.length > 0 ? values.categories : undefined,
          location: values.location?.name || undefined,
          locationLat: values.location?.latitude || undefined,
          locationLong: values.location?.longitude || undefined,
          myPlaces: values.myPlaces || undefined,
          needsReview: values.needsReview || undefined,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (!isAuthenticated && nextPage > 2) {
        const nextUrl = new URL(window.location.href)
        nextUrl.searchParams.set('page', String(nextPage))
        navigate({ to: '/auth/signup' } as never)
        return
      }
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [isAuthenticated, navigate]
  )

  const handleReset = useCallback(() => {
    navigate({ to: '/place/list', replace: true } as Parameters<typeof navigate>[0])
  }, [navigate])

  const getActiveTab = useCallback(() => {
    if (resolvedSearch.myPlaces) return 'myPlaces'
    return 'discover'
  }, [resolvedSearch.myPlaces])

  const getPagePreTitle = useCallback(() => {
    if (resolvedSearch.myPlaces) return t('My')
    return t('Discover')
  }, [resolvedSearch.myPlaces, t])

  const getPageSubtitle = useCallback(() => {
    if (resolvedSearch.myPlaces) return t('Browse places you have added or maintain.')
    return t(
      "Browse clubs, resorts, and lifestyle-friendly spots, filter by distance, tags, and what you're in the mood for."
    )
  }, [resolvedSearch.myPlaces, t])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <PlaceListSideNav
          defaultLocation={search.location ?? currentUser?.profile?.location}
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
          tags={resolvedPlaceTags}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[
              { label: t('Home'), to: '/' },
              { label: t('Place Categories'), to: '/place' },
              { label: t('Discover Places') },
            ]}
            className="mb-0 items-stretch md:mb-4"
            highlightTitle={t('Places')}
            preTitle={getPagePreTitle()}
            rightChild={
              <>
                <div className="flex flex-col items-end gap-3 md:hidden">
                  <PlaceMapViewButton />
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  <div className="flex gap-4">
                    <PlaceMapViewButton />
                    {isAuthenticated ? <CreatePlaceButton /> : null}
                  </div>

                  <div className="mt-auto">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                          <option value="DISTANCE">{t('Distance')}</option>
                          <option value="LATEST">{t('Latest')}</option>
                          <option value="NAME">{t('Name')}</option>
                          <option value="RATING">{t('Rating')}</option>
                        </NativeSelectFormField>
                      </Form>
                      <LayoutToggleButton
                        layout={placeCardLayout}
                        onToggle={() => setPlaceCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                      />
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={getPageSubtitle()}
          />

          <div className="my-5 w-full md:hidden">
            <PlaceListTopNav
              defaultLocation={search.location ?? currentUser?.profile?.location}
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              hasPermission={hasPermission}
              isAuthenticated={isAuthenticated}
              onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
              onReset={handleReset}
              rightBarChildren={
                <>
                  {isAuthenticated && <CreatePlaceButton />}
                  <LayoutToggleButton
                    layout={placeCardLayout}
                    onToggle={() => setPlaceCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                  />
                </>
              }
              tags={resolvedPlaceTags}
            />
          </div>

          <div className="mb-8">
            <PlacesMenu activeTab={getActiveTab()} />
          </div>

          {resolvedPlacesPage.places.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div
                className={
                  placeCardLayout === 'compact'
                    ? 'grid w-full grid-cols-1 items-stretch gap-4 md:gap-6'
                    : 'grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4'
                }
              >
                {resolvedPlacesPage.places?.map((place) => (
                  <div className="flex h-full w-full min-w-0 flex-col" key={place.id}>
                    <PlaceCard cardLayout={placeCardLayout} place={place} />
                  </div>
                ))}
              </div>

              <PaginationControl
                count={resolvedPlacesPage.count ? resolvedPlacesPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={resolvedSearch.page}
                pageSize={resolvedPlacesPage.perPage ? resolvedPlacesPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <MapPinHouseIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No places found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </div>
  )
}
